import React, { useState, useContext, useCallback } from "react";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Per-product sales report (daily / monthly).
 * Rows = products, Columns = periods. Shows quantity sold and amount per period.
 * Only counts orders with a successful, un-reversed payment (enforced server-side).
 */
function AdminProductSalesReport() {
    const { authFetch } = useContext(AuthContext);

    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const toInputDate = (d) => toLocalDateString(d);

    // DD/MM/YYYY HH:mm. toLocaleString() renders US format AND embeds a comma, which
    // splits the value across two cells when the CSV is opened in Excel.
    const formatDateTime = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date)) return "N/A";
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const [groupBy, setGroupBy] = useState("daily");
    const [startDate, setStartDate] = useState(toInputDate(firstOfMonth));
    const [endDate, setEndDate] = useState(toInputDate(today));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({}); // productID -> bool

    const toggleExpand = (productID) =>
        setExpanded(prev => ({ ...prev, [productID]: !prev[productID] }));

    const fetchReport = useCallback(async () => {
        if (!startDate || !endDate) {
            Swal.fire({ icon: "warning", title: "Select Dates", text: "Please choose a start and end date.", confirmButtonColor: "#45a049" });
            return;
        }
        if (new Date(startDate) > new Date(endDate)) {
            Swal.fire({ icon: "warning", title: "Invalid Range", text: "Start date cannot be after the end date.", confirmButtonColor: "#45a049" });
            return;
        }

        setLoading(true);
        try {
            const url = `${API_BASE_URL}/api/Report/productsales?start=${startDate}&end=${endDate}&groupBy=${groupBy}`;
            const res = await authFetch(url);
            if (!res.ok) throw new Error("Failed to load report");
            const json = await res.json();
            setData(json);
        } catch (e) {
            Swal.fire({ icon: "error", title: "Error", text: "Failed to load the product sales report.", confirmButtonColor: "#d33" });
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [authFetch, startDate, endDate, groupBy]);

    const formatCurrency = (v) =>
        `RM${(Number(v) || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // sales lookup: rowKey (productId|variantLabel) -> period -> { quantity, amount }
    const buildLookup = () => {
        const map = {};
        (data?.sales || []).forEach((s) => {
            if (!map[s.rowKey]) map[s.rowKey] = {};
            map[s.rowKey][s.period] = { quantity: s.quantity, amount: s.amount };
        });
        return map;
    };

    // Renders both figures in one cell: amount on top, quantity sold beneath
    const renderCell = (amount, quantity, opts = {}) => {
        if (amount === null || amount === undefined) return "-";
        return (
            <>
                <div style={{ fontWeight: opts.bold ? 600 : 400 }}>{formatCurrency(amount)}</div>
                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                    {quantity} sold
                </div>
            </>
        );
    };

    const cellValue = (lookup, rowKey, period) => {
        const cell = lookup[rowKey]?.[period];
        if (!cell) return "-";
        return renderCell(cell.amount, cell.quantity);
    };

    // Group variant rows under their parent product, and roll the variant figures
    // up into a per-period total for the collapsed parent row.
    const buildProductGroups = () => {
        const groups = [];
        const byId = {};

        (data?.products || []).forEach((row) => {
            if (!byId[row.groupId]) {
                byId[row.groupId] = {
                    groupId: row.groupId,
                    productID: row.productID,
                    name: row.name,
                    productCode: row.productCode,
                    rows: [],
                    totalQuantity: 0,
                    totalAmount: 0,
                    periodTotals: {},   // period -> { quantity, amount }
                };
                groups.push(byId[row.groupId]);
            }
            const g = byId[row.groupId];
            g.rows.push(row);
            g.totalQuantity += row.totalQuantity || 0;
            g.totalAmount += row.totalAmount || 0;
        });

        // Roll up each variant's per-period numbers into the parent
        (data?.sales || []).forEach((s) => {
            const g = byId[s.groupId];
            if (!g) return;
            if (!g.periodTotals[s.period]) g.periodTotals[s.period] = { quantity: 0, amount: 0 };
            g.periodTotals[s.period].quantity += s.quantity || 0;
            g.periodTotals[s.period].amount += s.amount || 0;
        });

        return groups;
    };

    const parentCellValue = (group, period) => {
        const cell = group.periodTotals[period];
        if (!cell) return "-";
        return renderCell(cell.amount, cell.quantity, { bold: true });
    };

    // A product only gets an expander when it actually has variant rows
    const hasVariants = (group) => group.rows.some(r => r.variantLabel);

    // Column total for a period (amount + quantity)
    const periodTotal = (period) => {
        const rows = (data?.sales || []).filter((s) => s.period === period);
        if (rows.length === 0) return "-";
        const amount = rows.reduce((t, r) => t + (r.amount || 0), 0);
        const quantity = rows.reduce((t, r) => t + (r.quantity || 0), 0);
        return renderCell(amount, quantity, { bold: true });
    };

    const handleExportCsv = () => {
        if (!data || !data.products?.length) {
            Swal.fire({ icon: "info", title: "No Data", text: "There is nothing to export.", confirmButtonColor: "#45a049" });
            return;
        }

        const esc = (v) => {
            if (v === null || v === undefined) return "";
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        // Force Excel to keep long codes as text (no scientific notation)
        const asText = (v) => (v === null || v === undefined || v === "" ? "" : `="${String(v).replace(/"/g, '""')}"`);

        const lookup = buildLookup();
        const periods = data.periods || [];

        let csv = "﻿";
        csv += "ARDENCY CONSTRUCTION AND TRADING SDN. BHD.\n";
        csv += `PRODUCT SALES REPORT (${data.groupBy === "monthly" ? "Monthly" : "Daily"})\n`;
        // Show the range actually covered (monthly reports are expanded to whole months)
        csv += `Period: ${data.effectiveStart || startDate} to ${data.effectiveEnd || endDate}\n`;
        if (data.rangeExpanded) {
            csv += `Note: Monthly report - range expanded to cover whole months\n`;
        }
        csv += `Generated on: ${formatDateTime(new Date())}\n\n`;

        // Each period gets two columns: Amount (RM) and Qty Sold
        const periodHeaders = periods.flatMap((p) => [`${p} (RM)`, `${p} Qty`]);
        csv += ["No.", "Product Code", "Product Name", "Variant", ...periodHeaders.map(esc), "Total (RM)", "Total Qty"]
            .join(",") + "\n";

        data.products.forEach((p, i) => {
            const cells = periods.flatMap((period) => {
                const cell = lookup[p.rowKey]?.[period];
                if (!cell) return ["0.00", "0"];
                return [Number(cell.amount).toFixed(2), cell.quantity];
            });
            csv += [
                i + 1,
                asText(p.productCode),
                esc(p.name),
                esc(p.variantLabel || "-"),
                ...cells,
                Number(p.totalAmount).toFixed(2),
                p.totalQuantity,
            ].join(",") + "\n";
        });

        // Grand total row
        const totalCells = periods.flatMap((period) => {
            const rows = (data.sales || []).filter((s) => s.period === period);
            return [
                rows.reduce((t, r) => t + (r.amount || 0), 0).toFixed(2),
                rows.reduce((t, r) => t + (r.quantity || 0), 0),
            ];
        });
        csv += [
            "TOTAL", "", "", "",
            ...totalCells,
            Number(data.grandTotalAmount).toFixed(2),
            data.grandTotalQuantity,
        ].join(",") + "\n";

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Ardency_Product_Sales_${data.groupBy}_${toLocalDateString(new Date())}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const lookup = buildLookup();
    const periods = data?.periods || [];
    const products = data?.products || [];
    const productGroups = buildProductGroups();

    const styles = {
        scrollContainer: { overflowX: "auto", width: "100%", maxWidth: "100%" },
        table: { width: "100%", borderCollapse: "collapse" },
        headerCell: {
            border: "1px solid #d1d5db", padding: "10px 16px", textAlign: "center",
            fontWeight: 600, color: "#374151", backgroundColor: "#f3f4f6",
            whiteSpace: "nowrap", minWidth: "120px",
        },
        stickyHeaderCell: {
            border: "1px solid #d1d5db", padding: "10px 16px", textAlign: "left",
            fontWeight: 600, color: "#374151", backgroundColor: "#f3f4f6",
            position: "sticky", left: 0, zIndex: 20, minWidth: "260px",
            textTransform: "uppercase", letterSpacing: "0.05em",
        },
        stickyBodyCell: {
            border: "1px solid #d1d5db", padding: "12px 16px", fontWeight: 500,
            color: "#1f2937", position: "sticky", left: 0, backgroundColor: "white",
            zIndex: 10, minWidth: "260px",
        },
        bodyCell: {
            border: "1px solid #d1d5db", padding: "12px 16px", textAlign: "center", color: "#374151",
        },
        totalCell: {
            border: "1px solid #d1d5db", padding: "12px 16px", textAlign: "center",
            fontWeight: 700, backgroundColor: "#f9fafb", color: "#111827",
        },
    };

    return (
        <div className="admin-sales-dashboard-trends-container" style={{ marginTop: "30px" }}>
            <div className="admin-sales-dashboard-card">
                <div className="admin-sales-dashboard-trends-header" style={{ flexWrap: "wrap", gap: "12px" }}>
                    <h3 style={{ margin: 0 }}>Product Sales Report</h3>

                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div className="admin-sales-dashboard-control-group">
                            <label>Report Type</label>
                            <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                <option value="daily">Daily</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>

                        <div className="admin-sales-dashboard-control-group">
                            <label>Start Date</label>
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>

                        <div className="admin-sales-dashboard-control-group">
                            <label>End Date</label>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>

                        <button className="admin-sales-dashboard-refresh-btn" onClick={fetchReport} disabled={loading}>
                            <i className="fas fa-sync-alt"></i> {loading ? "Loading..." : "Search"}
                        </button>

                        <button className="export-dropdown-item" onClick={handleExportCsv} style={{ whiteSpace: "nowrap" }}>
                            <i className="fas fa-file-csv"></i> Export CSV
                        </button>
                    </div>
                </div>

                {/* Monthly reports cover whole months, so tell the user the range was widened */}
                {data?.rangeExpanded && (
                    <div style={{
                        margin: "0 0 12px 0",
                        padding: "10px 14px",
                        background: "#e3f2fd",
                        border: "1px solid #90caf9",
                        borderRadius: "6px",
                        color: "#0d47a1",
                        fontSize: "13px",
                    }}>
                        <i className="fas fa-info-circle" style={{ marginRight: "6px" }}></i>
                        Monthly report covers <strong>whole months</strong>: {data.effectiveStart} – {data.effectiveEnd}
                    </div>
                )}

                {!data ? (
                    <div className="admin-sales-empty" style={{ padding: "30px", textAlign: "center", color: "#6b7280" }}>
                        <i className="fas fa-boxes" style={{ fontSize: "1.5rem" }}></i>
                        <p className="primary-message">Choose a date range and click Search to view product sales.</p>
                    </div>
                ) : products.length === 0 ? (
                    <div className="admin-sales-empty" style={{ padding: "30px", textAlign: "center", color: "#6b7280" }}>
                        <i className="fas fa-boxes" style={{ fontSize: "1.5rem" }}></i>
                        <p className="primary-message">No product sales found for the selected period.</p>
                    </div>
                ) : (
                    <div style={styles.scrollContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.stickyHeaderCell}>Product</th>
                                    {periods.map((p) => (
                                        <th key={p} style={styles.headerCell}>{p}</th>
                                    ))}
                                    <th style={styles.headerCell}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productGroups.map((g) => {
                                    const expandable = hasVariants(g);
                                    const isOpen = !!expanded[g.groupId];

                                    return (
                                        <React.Fragment key={g.groupId}>
                                            {/* Parent product row (totals across all its variants) */}
                                            <tr style={expandable ? { backgroundColor: "#fcfcfd" } : undefined}>
                                                <td style={styles.stickyBodyCell}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: "8px",
                                                            cursor: expandable ? "pointer" : "default",
                                                        }}
                                                        onClick={() => expandable && toggleExpand(g.groupId)}
                                                    >
                                                        {expandable ? (
                                                            <i
                                                                className={`fas fa-chevron-${isOpen ? "down" : "right"}`}
                                                                style={{ color: "#0288d1", marginTop: "4px", width: "12px" }}
                                                            ></i>
                                                        ) : (
                                                            <span style={{ width: "12px" }}></span>
                                                        )}
                                                        <div>
                                                            <div style={{ fontWeight: expandable ? 700 : 500 }}>{g.name}</div>
                                                            {g.productCode && (
                                                                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                                                    {g.productCode}
                                                                </div>
                                                            )}
                                                            {expandable && (
                                                                <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                                                                    {g.rows.length} variant{g.rows.length > 1 ? "s" : ""}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                {periods.map((period) => (
                                                    <td
                                                        key={period}
                                                        style={{ ...styles.bodyCell, fontWeight: expandable ? 600 : 400 }}
                                                    >
                                                        {parentCellValue(g, period)}
                                                    </td>
                                                ))}
                                                <td style={styles.totalCell}>
                                                    {renderCell(g.totalAmount, g.totalQuantity, { bold: true })}
                                                </td>
                                            </tr>

                                            {/* Variant rows (only when expanded) */}
                                            {expandable && isOpen && g.rows.map((r) => (
                                                <tr key={r.rowKey} style={{ backgroundColor: "#ffffff" }}>
                                                    <td style={{ ...styles.stickyBodyCell, paddingLeft: "44px" }}>
                                                        <span style={{ color: "#0288d1", fontSize: "13px" }}>
                                                            <i className="fas fa-level-up-alt fa-rotate-90" style={{ marginRight: "6px", opacity: 0.6 }}></i>
                                                            {r.variantLabel || "(no variant)"}
                                                        </span>
                                                    </td>
                                                    {periods.map((period) => (
                                                        <td key={period} style={{ ...styles.bodyCell, color: "#6b7280" }}>
                                                            {cellValue(lookup, r.rowKey, period)}
                                                        </td>
                                                    ))}
                                                    <td style={{ ...styles.totalCell, fontWeight: 500, color: "#6b7280" }}>
                                                        {renderCell(r.totalAmount, r.totalQuantity)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td style={{ ...styles.stickyBodyCell, fontWeight: 700, backgroundColor: "#f9fafb" }}>
                                        TOTAL
                                    </td>
                                    {periods.map((period) => (
                                        <td key={period} style={styles.totalCell}>{periodTotal(period)}</td>
                                    ))}
                                    <td style={styles.totalCell}>
                                        {renderCell(data.grandTotalAmount, data.grandTotalQuantity, { bold: true })}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminProductSalesReport;
